import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");
const goteborgKommunerText = getCityMunicipalityList("göteborg");

// Bortforsling av möbler per stad — /bortforsling-mobler-stockholm och
// /bortforsling-mobler-goteborg. Möbelspecifik vinkel (soffor, sängar,
// madrasser, garderober, bokhyllor, kontorsmöbler; isärmontering; trappor;
// skänka till second hand eller återvinning). Länkar till föräldern
// /bortforsling-mobler och stadens /bortforsling-{stad}.
export const bortforslingMoblerOrterPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-mobler-stockholm",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling av möbler Stockholm – Fast pris | Ungdomskraft",
      description:
        "Bortforsling av möbler i Stockholm. Vi bär ut soffor, sängar, garderober och madrasser – i trappa eller hiss, från Vasastan till Farsta. Pris direkt.",
      h1: "Bortforsling av möbler Stockholm",
    },

    hero: {
      heading: "Bortforsling av möbler i Stockholm",
      intro:
        "Ny soffa på väg, eller en hel möblering som ska ut? Vi hämtar möbler i hela Stockholm – från sekelskifteslägenheterna i Vasastan och på Östermalm till radhusen i Bromma och lägenheterna i Farsta och Hässelby. Vi monterar isär, bär ner och kör till second hand eller återvinning. Du ser priset direkt när du bokar.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft bär ut en soffa vid bortforsling av möbler i Stockholm",
    },

    itemsSection: {
      heading: "Möbler vi forslar bort i Stockholm",
      items: [
        {
          name: "Soffor",
          description: "Hörnsoffor, bäddsoffor, divansoffor och fåtöljer",
          image: "/sofa.webp",
          alt: "Hörnsoffa som bärs ner i trapphus vid bortforsling av möbler på Södermalm",
        },
        {
          name: "Sängar & madrasser",
          description: "Kontinentalsängar, ramsängar, resårbottnar och madrasser",
          image: "/bed.webp",
          alt: "Kontinentalsäng och madrass som hämtas vid bortforsling av möbler i Vasastan, Stockholm",
        },
        {
          name: "Garderober",
          description: "Garderobsskåp, byråer och förvaringssystem",
          image: "/wardrobe.webp",
          alt: "Garderob som monteras isär vid möbelbortforsling i Hägersten, Stockholm",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, soffbord, skrivbord och matstolar",
          image: "/table.webp",
          alt: "Matbord och stolar som forslas bort från lägenhet på Kungsholmen",
        },
        {
          name: "Kontorsmöbler",
          description: "Skrivbord, kontorsstolar, arkivskåp och hyllor",
          image: "/office-desk.webp",
          alt: "Kontorsskrivbord som hämtas vid bortforsling av kontorsmöbler i Kista, Stockholm",
        },
        {
          name: "Utemöbler",
          description: "Balkongmöbler, trädgårdsmöbler och solstolar",
          image: "/patio-furniture.webp",
          alt: "Utemöbler från en villa i Bromma som forslas bort",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling av möbler i Stockholm – vi bär, monterar isär och kör",
      mainText:
        "Att bli av med en soffa i Stockholm är svårare än det låter. Få stockholmare har en bil som rymmer en tresitssoffa, och i de flesta bostadsrättsföreningar får grovsoprummet inte användas för möbler – och i trapphuset får de inte stå, eftersom det är en utrymningsväg. Dessutom måste någon bära. I sekelskifteshusen i Vasastan, på Östermalm och Södermalm är hissen ofta för liten för något annat än en fåtölj, och i trevåningshusen från 40- och 50-talen i till exempel Hammarbyhöjden, Traneberg och Midsommarkransen finns ingen hiss alls. Bortforsling av möbler är därför en av våra vanligaste bokningar i staden.\n\nVi kommer med skåpbil och två personer. Garderober, kontinentalsängar och hörnsoffor som inte går genom dörren monterar vi isär på plats, med egna verktyg. Vi skyddar trappsteg, dörrkarmar och hissväggar med filtar, bär ner och lastar. Behöver du bara bli av med en säng eller en soffa räcker ofta paketet Liten; ska ett helt vardagsrum ut väljer du ett större paket. Priset ser du direkt när du bokar, och körsträcka inom 20 km ingår.\n\nMöbler i gott skick är för bra för att krossas i en container. Hela bokhyllor, matbord, byråer och fåtöljer kan vi köra till en second hand-butik som Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Stockholm, eller till en adress du själv väljer. Det som är trasigt, fläckigt eller slitet – till exempel gamla madrasser och spånskivemöbler som gått sönder – körs till godkänd återvinning där trä, metall och textil sorteras var för sig.",
      preparationHeading: "Inför möbelhämtningen i Stockholm",
      preparationText:
        "Töm lådor, skåp och hyllor innan vi kommer – en byrå går både lättare och snabbare att bära tom. Ta av sängkläder och lösa kuddar om de ska sparas. Du behöver inte montera isär något; det gör vi.\n\nTänk på vägen ut. Mät gärna hissen om du är osäker på om soffan får plats, och berätta om porten har kod, om det finns en innergård vi kan köra in på eller om gatan har lastzon. I innerstaden är det ofta där minuterna försvinner, så ju mer vi vet, desto snabbare går det.\n\nSka något till second hand? Säg det när du bokar och sätt en lapp på de möbler det gäller, så håller vi isär dem från det som ska till återvinning. Vill du ha kvar möbeltassar, nycklar eller skruvar till en möbel du säljer vidare, plocka undan dem först.",
      additionalSections: [
        {
          heading: "Möbelbortforsling i Stockholms stadsdelar och kranskommuner",
          content:
            "Vi hämtar möbler i hela Stockholm. I innerstaden – Södermalm, Kungsholmen, Vasastan, Östermalm och Gamla stan – handlar det ofta om smala trappor, små hissar och gator där det är svårt att stanna. Vi planerar parkeringen innan vi kör och bär effektivt så att bilen inte står längre än nödvändigt.\n\nI söderort (Årsta, Hägersten, Enskede, Farsta, Skarpnäck) och västerort (Bromma, Vällingby, Hässelby, Spånga) är det vanligare med radhus, källarförråd och garage, där möbler blivit stående efter en flytt. I kranskommuner som Solna, Sundbyberg, Nacka, Lidingö, Täby, Sollentuna och Huddinge gäller samma paketpriser.\n\nVill du bli av med mer än möbler – kartonger, vitvaror eller elektronik – kan du lägga det i samma bokning. Läs mer om vår bredare tjänst på sidan för bortforsling i Stockholm.",
          cta: { text: "Bortforsling i Stockholm", href: "/bortforsling-stockholm" },
        },
        {
          heading: "Skänka eller slänga möblerna?",
          content:
            "Bra möbler hittar ofta en ny ägare. Tumregeln är enkel: det som är helt, rent och fungerar kan gå till återbruk, det som är trasigt eller fläckigt går till återvinning.",
          listItems: [
            {
              label: "Till second hand –",
              text: "hela bokhyllor, byråer, matbord, stolar, fåtöljer och soffor utan fläckar eller skador. Vi kör dem till till exempel Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Stockholm. Kontakta gärna mottagaren i förväg om det gäller stora eller udda möbler.",
            },
            {
              label: "Till återvinning –",
              text: "trasiga möbler, gamla madrasser, spånskivemöbler som inte håller för en ny montering och klädda möbler med fläckar. Materialet sorteras på godkänd återvinning.",
            },
            {
              label: "RUT-avdrag –",
              text: "om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
            },
          ],
        },
        {
          heading: "Varför boka bortforsling av möbler i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Vi bär hela vägen –",
              text: "Från fjärde våningen utan hiss på Söder till källarförrådet i Bromma. Inga tillägg för trappor eller våningsplan.",
            },
            {
              label: "Isärmontering ingår –",
              text: "Garderober, sängar och hörnsoffor som inte går genom dörren monterar vi isär på plats med egna verktyg.",
            },
            {
              label: "Rätt ställe för varje möbel –",
              text: "Det hela går till second hand, det trasiga till återvinning. Ingenting hamnar i föreningens grovsoprum eller trapphus.",
            },
            {
              label: "Pris direkt, ofta samma dag –",
              text: "Välj paket efter hur många möbler det gäller och se priset direkt. Vi kan ofta komma redan samma dag, även kvällar och helger.",
            },
            {
              label: "Försäkrat –",
              text: "Alla uppdrag är försäkrade genom Trygg-Hansa, så trapphus, hiss och dörrkarmar är skyddade medan vi bär.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling av möbler i Stockholm?",
        answer:
          "Priset följer antalet möbler. En soffa eller en säng ryms oftast i Liten (995 kr, 1–2 föremål). 3–6 möbler är Mellan (1 495 kr) och 7–10 möbler Stor (2 495 kr). Ska ett helt rum tömmas passar Extra Stor (4 995 kr). Körsträcka inom 20 km ingår. Avfallsavgifter på återvinningen debiteras separat. Du ser priset direkt i bokningen.",
      },
      {
        question: "Vad kostar det att bli av med en soffa i Stockholm?",
        answer:
          "En soffa, eller en soffa och en fåtölj, ryms i paketet Liten för 995 kr med två personer och skåpbil. Vi bär ner den, monterar isär om det behövs och kör den till second hand eller återvinning.",
      },
      {
        question: "Får jag ställa möbler i grovsoprummet eller trapphuset?",
        answer:
          "Trapphuset är en utrymningsväg och ska hållas fritt, så där får möbler inte stå. Om grovsoprummet tar möbler bestämmer din förening eller hyresvärd, men i många föreningar är det inte tillåtet. Enklast är att vi hämtar möblerna direkt i lägenheten.",
      },
      {
        question: "Kan ni bära möbler i trappa utan hiss?",
        answer:
          "Ja. Vi är vana vid trevåningshusen utan hiss i Hammarbyhöjden, Traneberg och Midsommarkransen och vid de svängda trapporna i innerstadens sekelskifteshus. Vi bär oavsett våning, utan tillägg.",
      },
      {
        question: "Monterar ni isär garderober och sängar?",
        answer:
          "Ja, isärmontering ingår. Vi har med standardverktyg och tar isär garderober, kontinentalsängar, våningssängar, bokhyllor och skrivbord som inte går hela genom dörren eller hissen. Tiden räknas in i uppdraget.",
      },
      {
        question: "Tar ni madrasser och resårbottnar?",
        answer:
          "Ja. Madrasser, bäddmadrasser och resårbottnar tar vi med som vilka möbler som helst. Använda madrasser tas sällan emot av second hand, så de går oftast till återvinning.",
      },
      {
        question: "Kan ni köra mina möbler till Stadsmissionen eller Myrorna?",
        answer:
          "Ja. Ange önskad mottagare när du bokar, till exempel Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Stockholm. Kontakta gärna mottagaren i förväg för att bekräfta att de kan ta emot just dina möbler.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling av möbler i Stockholm?",
        answer:
          "Om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Forslar ni bort kontorsmöbler?",
        answer:
          "Ja, vi hämtar skrivbord, kontorsstolar, arkivskåp, hyllor och konferensbord från kontor i hela Stockholm, till exempel i city, Kista och Solna. Är det ett helt kontor som ska tömmas har vi en egen tjänst för det.",
      },
      {
        question: "Hur snabbt kan ni hämta möblerna?",
        answer:
          "Ofta redan samma dag. Du kan boka med kort varsel eller upp till 30 dagar framåt, och du ser lediga tider direkt i bokningen – även kvällar och helger.",
      },
      {
        question: "Hämtar ni möbler från källarförråd och vind?",
        answer:
          "Ja. Många stockholmare har gamla möbler stående i föreningens källar- eller vindsförråd. Se till att vi har nyckel eller tagg och berätta vilket förråd det gäller.",
      },
      {
        question: "Kan jag boka både möbler och annat skräp samtidigt?",
        answer:
          "Ja, lägg bara in allt i samma bokning och välj paket efter den totala mängden. Kartonger, vitvaror och elektronik följer med i samma körning.",
      },
      {
        question: "Behöver jag vara hemma när ni hämtar?",
        answer:
          "Det är bäst om du eller någon annan är på plats och kan visa vilka möbler som ska med. Står de i ett öppet förråd eller en tom lägenhet går det också bra att lämna tydliga instruktioner och nycklar.",
      },
      {
        question: "Vilka kommuner runt Stockholm hämtar ni möbler i?",
        answer: `Vi hämtar möbler i hela Stockholmsregionen: ${stockholmKommunerText}. Samma pris i alla kommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Bortforsling av möbler i Stockholm i 3 steg",
      step2Text:
        "Vi monterar isär det som inte går genom dörren, bär ner möblerna i trapphus eller hiss och lastar skåpbilen. Du pekar bara på det som ska bort.",
      ctaHeading: "Boka bortforsling av möbler i Stockholm",
      ctaText:
        "Fyll i adressen, välj paket efter antal möbler och boka en tid. Priset syns direkt – ingen offert behövs.",
    },

    en: {
      slug: "furniture-removal-stockholm",
      meta: {
        title: "Furniture Removal Stockholm – Fixed Price | Ungdomskraft",
        description:
          "Furniture removal in Stockholm. We carry out sofas, beds, wardrobes and mattresses – by stairs or lift, from Vasastan to Farsta. Instant price.",
        h1: "Furniture Removal Stockholm",
      },
      hero: {
        heading: "Furniture removal in Stockholm",
        intro:
          "New sofa on the way, or a whole set of furniture that needs to go? We collect furniture across Stockholm – from the turn-of-the-century flats in Vasastan and on Östermalm to the terraced houses in Bromma and the flats in Farsta and Hässelby. We take apart, carry down and drive to second hand or recycling. You see the price as soon as you book.",
        heroImageAlt:
          "Ungdomskraft carrying out a sofa during furniture removal in Stockholm",
      },
      itemsSection: {
        heading: "Furniture we remove in Stockholm",
        items: [
          {
            name: "Sofas",
            description: "Corner sofas, sofa beds, chaise sofas and armchairs",
            image: "/sofa.webp",
            alt: "Corner sofa carried down a stairwell during furniture removal on Södermalm",
          },
          {
            name: "Beds & mattresses",
            description: "Continental beds, bed frames, box springs and mattresses",
            image: "/bed.webp",
            alt: "Continental bed and mattress collected during furniture removal in Vasastan, Stockholm",
          },
          {
            name: "Wardrobes",
            description: "Wardrobes, chests of drawers and storage systems",
            image: "/wardrobe.webp",
            alt: "Wardrobe taken apart during furniture removal in Hägersten, Stockholm",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, coffee tables, desks and dining chairs",
            image: "/table.webp",
            alt: "Dining table and chairs removed from a flat on Kungsholmen",
          },
          {
            name: "Office furniture",
            description: "Desks, office chairs, filing cabinets and shelving",
            image: "/office-desk.webp",
            alt: "Office desk collected during office furniture removal in Kista, Stockholm",
          },
          {
            name: "Outdoor furniture",
            description: "Balcony furniture, garden furniture and sun loungers",
            image: "/patio-furniture.webp",
            alt: "Outdoor furniture removed from a house in Bromma",
          },
        ],
      },
      seoContent: {
        mainHeading: "Furniture removal in Stockholm – we carry, take apart and drive",
        mainText:
          "Getting rid of a sofa in Stockholm is harder than it sounds. Few Stockholmers have a car that fits a three-seater, and in most housing associations the bulky-waste room may not be used for furniture – and it cannot stand in the stairwell, since that is an escape route. And someone has to carry it. In the turn-of-the-century buildings in Vasastan, on Östermalm and Södermalm the lift is often too small for anything bigger than an armchair, and in the three-storey blocks from the 40s and 50s in places like Hammarbyhöjden, Traneberg and Midsommarkransen there is no lift at all. That is why furniture removal is one of our most common bookings in the city.\n\nWe come with a van and two people. Wardrobes, continental beds and corner sofas that won't go through the door are taken apart on site with our own tools. We protect steps, door frames and lift walls with blankets, carry down and load. If you only need to get rid of a bed or a sofa, the Small package is often enough; if a whole living room is going, choose a larger package. You see the price as soon as you book, and driving within 20 km is included.\n\nFurniture in good condition is too good to be crushed in a container. Intact bookcases, dining tables, chests of drawers and armchairs can go to a second-hand shop such as Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Stockholm, or to an address of your choice. Anything broken, stained or worn – such as old mattresses and chipboard furniture that has come apart – goes to approved recycling, where wood, metal and textiles are sorted separately.",
        preparationHeading: "Before the furniture pickup in Stockholm",
        preparationText:
          "Empty drawers, cupboards and shelves before we arrive – a chest of drawers is both lighter and quicker to carry empty. Take off bedding and loose cushions if you want to keep them. You don't need to take anything apart; we do that.\n\nThink about the way out. Measure the lift if you are unsure whether the sofa fits, and tell us if the entrance has a code, if there is a courtyard we can drive into or if the street has a loading zone. In the inner city that is often where the minutes go, so the more we know, the faster it goes.\n\nAnything going to second hand? Tell us when booking and put a note on those pieces, and we will keep them apart from what goes to recycling. If you want to keep furniture feet, keys or screws for a piece you are passing on, set them aside first.",
        additionalSections: [
          {
            heading: "Furniture removal across Stockholm's districts and neighbouring municipalities",
            content:
              "We collect furniture all over Stockholm. In the inner city – Södermalm, Kungsholmen, Vasastan, Östermalm and Gamla stan – it is often narrow stairs, small lifts and streets where it is hard to stop. We plan parking before we set off and carry efficiently so the van is not parked longer than needed.\n\nIn the southern suburbs (Årsta, Hägersten, Enskede, Farsta, Skarpnäck) and the western suburbs (Bromma, Vällingby, Hässelby, Spånga) terraced houses, basement storage and garages are more common, where furniture has been left after a move. In neighbouring municipalities such as Solna, Sundbyberg, Nacka, Lidingö, Täby, Sollentuna and Huddinge the same package prices apply.\n\nWant to get rid of more than furniture – boxes, appliances or electronics? Add it to the same booking. Read more about our broader service on the junk removal in Stockholm page.",
            cta: { text: "Junk removal in Stockholm", href: "/bortforsling-stockholm" },
          },
          {
            heading: "Donate or dispose of the furniture?",
            content:
              "Good furniture often finds a new owner. The rule of thumb is simple: what is intact, clean and works can go to reuse, what is broken or stained goes to recycling.",
            listItems: [
              {
                label: "To second hand –",
                text: "intact bookcases, chests of drawers, dining tables, chairs, armchairs and sofas without stains or damage. We drive them to, for example, Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Stockholm. Contact the recipient in advance if it is large or unusual furniture.",
              },
              {
                label: "To recycling –",
                text: "broken furniture, old mattresses, chipboard furniture that won't survive reassembly and upholstered furniture with stains. The material is sorted at approved recycling.",
              },
              {
                label: "RUT deduction –",
                text: "if your furniture is transported to a second-hand shop, charity or another location for reuse, you are eligible for the RUT tax deduction on the labour cost. Standard furniture removal where items go directly to a recycling station does not qualify. The deduction is applied directly on the invoice.",
              },
            ],
          },
          {
            heading: "Why book furniture removal in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "We carry all the way –",
                text: "From the fourth floor without a lift on Södermalm to the basement storage in Bromma. No surcharge for stairs or floors.",
              },
              {
                label: "Taking apart is included –",
                text: "Wardrobes, beds and corner sofas that won't go through the door are taken apart on site with our own tools.",
              },
              {
                label: "The right place for every piece –",
                text: "Intact pieces go to second hand, broken ones to recycling. Nothing ends up in the association's bulky-waste room or stairwell.",
              },
              {
                label: "Instant price, often same day –",
                text: "Pick a package by how many pieces there are and see the price directly. We can often come the same day, including evenings and weekends.",
              },
              {
                label: "Insured –",
                text: "All jobs are insured through Trygg-Hansa, so stairwell, lift and door frames are protected while we carry.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does furniture removal in Stockholm cost?",
          answer:
            "The price follows the number of pieces. A sofa or a bed usually fits in Small (SEK 995, 1–2 items). 3–6 pieces is Medium (SEK 1,495) and 7–10 pieces Large (SEK 2,495). If a whole room is being cleared, Extra Large (SEK 4,995) fits. Driving within 20 km is included. Waste fees at the recycling centre are charged separately. You see the price directly in the booking.",
        },
        {
          question: "How much does it cost to get rid of a sofa in Stockholm?",
          answer:
            "A sofa, or a sofa and an armchair, fits in the Small package for SEK 995 with two people and a van. We carry it down, take it apart if needed and drive it to second hand or recycling.",
        },
        {
          question: "Can I leave furniture in the bulky-waste room or stairwell?",
          answer:
            "The stairwell is an escape route and must be kept clear, so furniture may not be left there. Whether the bulky-waste room takes furniture is up to your association or landlord, but many associations do not allow it. The easiest option is for us to collect the furniture straight from the flat.",
        },
        {
          question: "Can you carry furniture on stairs without a lift?",
          answer:
            "Yes. We are used to the three-storey blocks without a lift in Hammarbyhöjden, Traneberg and Midsommarkransen and the curved stairs of the inner city's older buildings. We carry regardless of floor, with no surcharge.",
        },
        {
          question: "Do you take apart wardrobes and beds?",
          answer:
            "Yes, taking apart is included. We bring standard tools and dismantle wardrobes, continental beds, bunk beds, bookcases and desks that won't go through the door or lift in one piece. The time is counted in the job.",
        },
        {
          question: "Do you take mattresses and box springs?",
          answer:
            "Yes. Mattresses, toppers and box springs come along like any other furniture. Used mattresses are rarely accepted by second-hand shops, so they usually go to recycling.",
        },
        {
          question: "Can you drive my furniture to Stadsmissionen or Myrorna?",
          answer:
            "Yes. Name the recipient when booking, for example Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Stockholm. It is a good idea to contact the recipient in advance to confirm they can accept your particular furniture.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture removal in Stockholm?",
          answer:
            "If your furniture is transported to a second-hand shop, charity or another location for reuse, you are eligible for the RUT tax deduction on the labour cost. Standard furniture removal where items go directly to a recycling station does not qualify. The deduction is applied directly on the invoice.",
        },
        {
          question: "Do you remove office furniture?",
          answer:
            "Yes, we collect desks, office chairs, filing cabinets, shelving and conference tables from offices across Stockholm, for example in the city centre, Kista and Solna. If a whole office needs clearing, we have a separate service for that.",
        },
        {
          question: "How quickly can you collect the furniture?",
          answer:
            "Often the same day. You can book at short notice or up to 30 days ahead, and you see available times directly in the booking – evenings and weekends included.",
        },
        {
          question: "Do you collect furniture from basement storage and attics?",
          answer:
            "Yes. Many Stockholmers have old furniture sitting in the association's basement or attic storage. Make sure we have a key or fob and tell us which storage unit it is.",
        },
        {
          question: "Can I book furniture and other junk at the same time?",
          answer:
            "Yes, just add everything to the same booking and pick a package by the total amount. Boxes, appliances and electronics come along in the same run.",
        },
        {
          question: "Do I need to be home when you collect?",
          answer:
            "It is best if you or someone else is there to show which pieces should go. If they are in an open storage unit or an empty flat, clear instructions and keys work too.",
        },
        {
          question: "Which municipalities around Stockholm do you collect furniture in?",
          answer:
            "We collect furniture across the whole Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. Same price in every municipality.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Furniture removal in Stockholm in 3 steps",
        step2Text:
          "We take apart what won't go through the door, carry the furniture down by stairs or lift and load the van. You just point at what should go.",
        ctaHeading: "Book furniture removal in Stockholm",
        ctaText:
          "Enter the address, pick a package by the number of pieces and book a time. The price shows instantly – no quote needed.",
      },
    },
  },

  {
    slug: "bortforsling-mobler-goteborg",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling av möbler Göteborg – Pris direkt | Ungdomskraft",
      description:
        "Bortforsling av möbler i Göteborg. Vi hämtar soffor, sängar och garderober i Majorna, Linné, på Hisingen och i Frölunda. Skänkt eller återvunnet.",
      h1: "Bortforsling av möbler Göteborg",
    },

    hero: {
      heading: "Bortforsling av möbler i Göteborg",
      intro:
        "Ska du slänga en soffa i Göteborg, eller behöver du hämtning av möbler efter en flytt? Vi kommer till dig i Linnéstaden, Majorna, Johanneberg, på Hisingen eller i Frölunda, monterar isär det som behövs och bär ut. Hela möbler kan gå till second hand, resten till återvinning. Välj paket och se priset direkt.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft lastar möbler i skåpbil vid bortforsling av möbler i Göteborg",
    },

    itemsSection: {
      heading: "Möbler vi hämtar i Göteborg",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Tresitsiga, hörnsoffor, bäddsoffor och fåtöljer",
          image: "/sofa.webp",
          alt: "Soffa som bärs ut ur landshövdingehus i Majorna vid bortforsling av möbler i Göteborg",
        },
        {
          name: "Sängar",
          description: "Dubbelsängar, våningssängar, bäddmadrasser och resårbottnar",
          image: "/bed.webp",
          alt: "Säng och madrass som hämtas vid möbelbortforsling i Linnéstaden, Göteborg",
        },
        {
          name: "Skåp & byråer",
          description: "Garderober, byråer, vitrinskåp och kommoder",
          image: "/wardrobe.webp",
          alt: "Garderob som bärs ut vid bortforsling av möbler på Hisingen, Göteborg",
        },
        {
          name: "Fåtöljer & stolar",
          description: "Läsfåtöljer, matstolar, pallar och gungstolar",
          image: "/armchair.webp",
          alt: "Fåtölj som skänks till second hand vid möbelhämtning i Johanneberg, Göteborg",
        },
        {
          name: "Bokhyllor & bord",
          description: "Bokhyllor, matbord, soffbord och TV-bänkar",
          image: "/furniture.webp",
          alt: "Bokhylla och bord som forslas bort från lägenhet i Guldheden, Göteborg",
        },
        {
          name: "Kontorsmöbler",
          description: "Skrivbord, kontorsstolar och förvaring från hemmakontor och företag",
          image: "/office-desk.webp",
          alt: "Skrivbord som hämtas vid bortforsling av kontorsmöbler i Göteborg",
        },
      ],
    },

    seoContent: {
      mainHeading: "Hämtning av möbler i Göteborg – från landshövdingehus till villa",
      mainText:
        "Göteborg är ingen lätt stad att bära möbler i. Landshövdingehusen i Majorna, Kungsladugård, Olskroken och Landala har smala trätrappor som svänger och sällan hiss. I Guldheden, Johanneberg och Masthugget är backarna så branta att det är tungt att ens gå med en kartong. Och har man ingen bil, eller bara en liten, finns det få sätt att själv få en bäddsoffa till återvinningscentralen. Därför bokar många göteborgare hjälp när en möbel ska bort.\n\nVi kommer med skåpbil och två personer och tar hand om allt från enstaka fåtöljer till hela möbleringar. Det som inte går genom en trång dörr – en garderob, en ramsäng, en hörnsoffa – monterar vi isär på plats. Trappsteg och väggar skyddas med filtar medan vi bär. En soffa eller en säng ryms oftast i paketet Liten, och flera möbler i Mellan eller Stor. Priset visas direkt i bokningen, och de första 20 kilometrarna ingår.\n\nMånga möbler som ska bort är fortfarande fina. I Göteborg finns flera ställen som tar emot dem: Göteborgs Stadsmission, som bland annat har en butik i Kretsloppsparken Alelyckan, Myrorna, Erikshjälpen och Emmaus Björkå. Vi kör dit det som är helt och rent, eller till en adress du själv anger. Det som är trasigt, slitet eller fläckigt – gamla madrasser, lösa spånskivor, en soffa som gjort sitt – körs till godkänd återvinning.",
      preparationHeading: "Så gör du inför möbelhämtningen i Göteborg",
      preparationText:
        "Töm byråer, skåp och bokhyllor innan vi kommer. Lösa hyllplan och lådor kan ligga kvar i möbeln, men innehållet behöver vara ute.\n\nBerätta hur vi kommer fram. Bor du i ett landshövdingehus eller ett äldre hyreshus, nämn om porten har kod och om gården går att köra in på. I backarna kring Guldheden och Masthugget och i de centrala delarna kring Linnégatan och Vasaplatsen är gatuparkeringen ofta full, så har du tillgång till en infart eller gästplats är det guld värt. På Hisingen och i villaområdena räcker det ofta att vi får backa upp på uppfarten.\n\nMärk de möbler som ska till second hand, så håller vi isär dem från resten. Möbler med fästen i väggen, som bokhyllor och väggskåp, går gärna att lossa innan vi kommer – annars gör vi det, men säg till i bokningen.",
      additionalSections: [
        {
          heading: "Här hämtar vi möbler i Göteborg",
          content:
            "Vi hämtar möbler i hela Göteborg. I innerstaden och de gamla arbetarkvarteren – Linnéstaden, Haga, Vasastaden, Majorna, Kungsladugård och Olskroken – är trapporna smala och gatorna trånga. Där bär vi försiktigt och planerar var bilen ska stå.\n\nPå Hisingen, i Lundby, Kvillebäcken, Biskopsgården, Backa och Torslanda, är det ofta nyare lägenheter med hiss eller radhus med förråd. Öster om älven jobbar vi i Gamlestaden, Kortedala och Bergsjön, och i söder i Högsbo, Västra Frölunda och Askim. Mölndal, Partille och Kungälv har samma paketpriser.\n\nSka du bli av med mer än möbler – kartonger, vitvaror eller trädgårdsavfall? Då passar vår vanliga bortforsling i Göteborg, där allt kan följa med i samma körning.",
          cta: { text: "Bortforsling i Göteborg", href: "/bortforsling-goteborg" },
        },
        {
          heading: "Slänga soffa i Göteborg – eller ge den ett nytt hem?",
          content:
            "En soffa är det vanligaste vi hämtar i Göteborg. Den är för stor för grovsoprummet, för tung att bära själv och passar inte i en vanlig bil. Så här tänker vi när vi kör iväg den:",
          listItems: [
            {
              label: "Hel och ren –",
              text: "går till second hand, till exempel Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Björkå. Kontakta gärna mottagaren i förväg om soffan är stor, eftersom alla butiker inte har plats för allt.",
            },
            {
              label: "Sliten, trasig eller fläckig –",
              text: "går till godkänd återvinning. Klädda möbler sorteras där efter material.",
            },
            {
              label: "RUT-avdrag –",
              text: "om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
            },
          ],
          afterContent:
            "Vill du läsa mer om hur vi hanterar olika typer av möbler, från kontorsmöbler till utemöbler? Se vår huvudsida för bortforsling av möbler.",
          cta: { text: "Bortforsling av möbler", href: "/bortforsling-mobler" },
        },
        {
          heading: "Varför boka bortforsling av möbler i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Vi känner trapporna –",
              text: "Trätrapporna i landshövdingehusen och backarna i Guldheden är vardag för oss. Inga tillägg för våning eller trappa.",
            },
            {
              label: "Isärmontering på köpet –",
              text: "Garderober, sängar och hörnsoffor som inte ryms genom dörren tar vi isär på plats med egna verktyg.",
            },
            {
              label: "Second hand först –",
              text: "Hela möbler kan köras till Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Björkå i stället för till återvinningen.",
            },
            {
              label: "Pris direkt i bokningen –",
              text: "Välj paket efter antal möbler och se priset på en gång. Ofta kan vi komma samma dag, även helger.",
            },
            {
              label: "Försäkrat genom Trygg-Hansa –",
              text: "Väggar, trapphus och dörrar är skyddade under hela hämtningen.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling av möbler i Göteborg?",
        answer:
          "Du väljer paket efter antal möbler: Liten (995 kr) för 1–2 möbler, Mellan (1 495 kr) för 3–6 och Stor (2 495 kr) för 7–10. Ska ett helt rum eller garage tömmas passar Extra Stor (4 995 kr). Körsträcka inom 20 km ingår. Avfallsavgifter på återvinningen debiteras separat. Priset syns direkt när du bokar.",
      },
      {
        question: "Hur slänger jag en soffa i Göteborg utan bil?",
        answer:
          "Boka hämtning hos oss. En soffa ryms i paketet Liten (995 kr) med två personer och skåpbil. Vi bär ut den, tar isär den om den inte går genom dörren och kör den till second hand eller återvinning.",
      },
      {
        question: "Erbjuder ni hämtning av möbler i hela Göteborg?",
        answer:
          "Ja, i alla stadsdelar – från Linnéstaden, Majorna och Olskroken till Hisingen, Kortedala, Västra Frölunda och Askim – och i kranskommunerna. Samma pris överallt.",
      },
      {
        question: "Klarar ni landshövdingehus utan hiss?",
        answer:
          "Ja. Trätrapporna i Majorna, Kungsladugård och Olskroken är smala och svänger, så vi tar isär större möbler och skyddar trappsteg och väggar. Inget tillägg för våningsplan.",
      },
      {
        question: "Tar ni isär möblerna åt mig?",
        answer:
          "Ja, isärmontering ingår. Vi har med verktyg och tar isär garderober, sängar, bokhyllor och skrivbord som behöver det för att komma ut. Om en möbel kräver specialverktyg, säg till när du bokar.",
      },
      {
        question: "Kan ni köra mina möbler till second hand i Göteborg?",
        answer:
          "Ja. Ange mottagare i bokningen, till exempel Göteborgs Stadsmission, Myrorna, Erikshjälpen eller Emmaus Björkå. Hör gärna av dig till mottagaren i förväg om det gäller stora möbler.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling av möbler i Göteborg?",
        answer:
          "Om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hämtar ni madrasser?",
        answer:
          "Ja, madrasser, bäddmadrasser och resårbottnar tar vi med. Begagnade madrasser tas sällan emot av second hand, så de går oftast till återvinning.",
      },
      {
        question: "Kan jag ställa möblerna i grovsoprummet i stället?",
        answer:
          "Det avgör din hyresvärd eller förening, och många tillåter inte möbler där. Trapphus och källargångar ska alltid hållas fria. Det enklaste är att vi hämtar direkt i lägenheten.",
      },
      {
        question: "Hämtar ni från vindskontor och källarförråd?",
        answer:
          "Ja, vi hämtar möbler från vindskontor, källare, förråd och garage. Se till att vi har nyckel och berätta var utrymmet ligger.",
      },
      {
        question: "Hämtar ni kontorsmöbler i Göteborg?",
        answer:
          "Ja, från hemmakontor och företag i hela Göteborg: skrivbord, kontorsstolar, arkivskåp och hyllor. Ska ett helt kontor tömmas har vi en egen tjänst för det.",
      },
      {
        question: "Hur snart kan ni hämta?",
        answer:
          "Ofta samma dag. Du ser lediga tider direkt i bokningen och kan boka upp till 30 dagar framåt, även kvällar och helger.",
      },
      {
        question: "Kan ni ta med annat än möbler i samma bokning?",
        answer:
          "Ja, lägg till kartonger, vitvaror eller elektronik i samma bokning och välj paket efter den totala mängden.",
      },
      {
        question: "Vilka kommuner i Göteborgsregionen hämtar ni möbler i?",
        answer: `Vi hämtar möbler i hela Göteborgsregionen: ${goteborgKommunerText}. Inga tillägg för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Bortforsling av möbler i Göteborg i 3 steg",
      step2Text:
        "Vi tar isär det som behövs, bär ut möblerna – även i landshövdingehusens trätrappor – och lastar skåpbilen. Hela möbler kan gå till second hand.",
      ctaHeading: "Boka hämtning av möbler i Göteborg",
      ctaText:
        "Skriv in adressen, välj paket efter antal möbler och välj en tid. Priset syns direkt, utan offert.",
    },

    en: {
      slug: "furniture-removal-gothenburg",
      meta: {
        title: "Furniture Removal Gothenburg – Instant Price | Ungdomskraft",
        description:
          "Furniture removal in Gothenburg. We collect sofas, beds and wardrobes in Majorna, Linné, on Hisingen and in Frölunda. Donated or recycled.",
        h1: "Furniture Removal Gothenburg",
      },
      hero: {
        heading: "Furniture removal in Gothenburg",
        intro:
          "Need to get rid of a sofa in Gothenburg, or furniture collected after a move? We come to you in Linnéstaden, Majorna, Johanneberg, on Hisingen or in Frölunda, take apart what needs it and carry it out. Intact furniture can go to second hand, the rest to recycling. Pick a package and see the price instantly.",
        heroImageAlt:
          "Ungdomskraft loading furniture into a van during furniture removal in Gothenburg",
      },
      itemsSection: {
        heading: "Furniture we collect in Gothenburg",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Three-seaters, corner sofas, sofa beds and armchairs",
            image: "/sofa.webp",
            alt: "Sofa carried out of a landshövdingehus in Majorna during furniture removal in Gothenburg",
          },
          {
            name: "Beds",
            description: "Double beds, bunk beds, toppers and box springs",
            image: "/bed.webp",
            alt: "Bed and mattress collected during furniture removal in Linnéstaden, Gothenburg",
          },
          {
            name: "Cabinets & drawers",
            description: "Wardrobes, chests of drawers, display cabinets and dressers",
            image: "/wardrobe.webp",
            alt: "Wardrobe carried out during furniture removal on Hisingen, Gothenburg",
          },
          {
            name: "Armchairs & chairs",
            description: "Reading chairs, dining chairs, stools and rocking chairs",
            image: "/armchair.webp",
            alt: "Armchair donated to second hand during a furniture pickup in Johanneberg, Gothenburg",
          },
          {
            name: "Bookcases & tables",
            description: "Bookcases, dining tables, coffee tables and TV benches",
            image: "/furniture.webp",
            alt: "Bookcase and table removed from a flat in Guldheden, Gothenburg",
          },
          {
            name: "Office furniture",
            description: "Desks, office chairs and storage from home offices and businesses",
            image: "/office-desk.webp",
            alt: "Desk collected during office furniture removal in Gothenburg",
          },
        ],
      },
      seoContent: {
        mainHeading: "Furniture collection in Gothenburg – from landshövdingehus to villa",
        mainText:
          "Gothenburg is not an easy city to carry furniture in. The landshövdingehus buildings in Majorna, Kungsladugård, Olskroken and Landala have narrow timber stairs that turn and rarely a lift. In Guldheden, Johanneberg and Masthugget the hills are so steep that even walking with a box is hard work. And without a car, or with only a small one, there are few ways to get a sofa bed to the recycling centre yourself. That is why many Gothenburgers book help when a piece of furniture has to go.\n\nWe come with a van and two people and handle everything from a single armchair to a whole home's furniture. What won't go through a narrow door – a wardrobe, a bed frame, a corner sofa – we take apart on site. Steps and walls are protected with blankets while we carry. A sofa or a bed usually fits in the Small package, and several pieces in Medium or Large. The price shows directly in the booking, and the first 20 kilometres are included.\n\nMuch of the furniture that has to go is still in good shape. Gothenburg has several places that accept it: Göteborgs Stadsmission, which among other things has a shop in Kretsloppsparken Alelyckan, Myrorna, Erikshjälpen and Emmaus Björkå. We drive what is intact and clean there, or to an address you choose. What is broken, worn or stained – old mattresses, loose chipboard, a sofa that has done its time – goes to approved recycling.",
        preparationHeading: "Getting ready for the furniture pickup in Gothenburg",
        preparationText:
          "Empty chests of drawers, cupboards and bookcases before we arrive. Loose shelves and drawers can stay in the furniture, but the contents need to be out.\n\nTell us how to reach you. If you live in a landshövdingehus or an older rental block, mention whether the entrance has a code and whether we can drive into the courtyard. On the hills around Guldheden and Masthugget and in the central areas around Linnégatan and Vasaplatsen street parking is often full, so access to a driveway or visitor space is worth a lot. On Hisingen and in the villa areas it is often enough that we can reverse onto the driveway.\n\nMark the pieces going to second hand and we will keep them apart from the rest. Furniture fixed to the wall, such as bookcases and wall cabinets, is best loosened before we arrive – otherwise we will do it, but mention it in the booking.",
        additionalSections: [
          {
            heading: "Where we collect furniture in Gothenburg",
            content:
              "We collect furniture across Gothenburg. In the inner city and the old working-class districts – Linnéstaden, Haga, Vasastaden, Majorna, Kungsladugård and Olskroken – the stairs are narrow and the streets tight. There we carry carefully and plan where the van will stand.\n\nOn Hisingen, in Lundby, Kvillebäcken, Biskopsgården, Backa and Torslanda, it is often newer flats with a lift or terraced houses with storage. East of the river we work in Gamlestaden, Kortedala and Bergsjön, and to the south in Högsbo, Västra Frölunda and Askim. Mölndal, Partille and Kungälv have the same package prices.\n\nNeed to get rid of more than furniture – boxes, appliances or garden waste? Then our regular junk removal in Gothenburg fits, where everything can go in the same run.",
            cta: { text: "Junk removal in Gothenburg", href: "/bortforsling-goteborg" },
          },
          {
            heading: "Throw out a sofa in Gothenburg – or give it a new home?",
            content:
              "A sofa is the most common thing we collect in Gothenburg. It is too big for the bulky-waste room, too heavy to carry alone and won't fit in an ordinary car. This is how we think when we drive it away:",
            listItems: [
              {
                label: "Intact and clean –",
                text: "goes to second hand, for example Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Björkå. Contact the recipient in advance if the sofa is large, since not every shop has room for everything.",
              },
              {
                label: "Worn, broken or stained –",
                text: "goes to approved recycling. Upholstered furniture is sorted there by material.",
              },
              {
                label: "RUT deduction –",
                text: "if your furniture is transported to a second-hand shop, charity or another location for reuse, you are eligible for the RUT tax deduction on the labour cost. Standard furniture removal where items go directly to a recycling station does not qualify. The deduction is applied directly on the invoice.",
              },
            ],
            afterContent:
              "Want to read more about how we handle different kinds of furniture, from office furniture to outdoor furniture? See our main furniture removal page.",
            cta: { text: "Furniture removal", href: "/bortforsling-mobler" },
          },
          {
            heading: "Why book furniture removal in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "We know the stairs –",
                text: "The timber stairs of the landshövdingehus and the hills of Guldheden are everyday work for us. No surcharge for floors or stairs.",
              },
              {
                label: "Taking apart included –",
                text: "Wardrobes, beds and corner sofas that won't fit through the door are taken apart on site with our own tools.",
              },
              {
                label: "Second hand first –",
                text: "Intact furniture can go to Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Björkå instead of to recycling.",
              },
              {
                label: "Instant price in the booking –",
                text: "Pick a package by the number of pieces and see the price at once. We can often come the same day, weekends included.",
              },
              {
                label: "Insured through Trygg-Hansa –",
                text: "Walls, stairwell and doors are protected throughout the pickup.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does furniture removal in Gothenburg cost?",
          answer:
            "You pick a package by the number of pieces: Small (SEK 995) for 1–2 pieces, Medium (SEK 1,495) for 3–6 and Large (SEK 2,495) for 7–10. If a whole room or garage is being cleared, Extra Large (SEK 4,995) fits. Driving within 20 km is included. Waste fees at the recycling centre are charged separately. The price shows as soon as you book.",
        },
        {
          question: "How do I get rid of a sofa in Gothenburg without a car?",
          answer:
            "Book a pickup with us. A sofa fits in the Small package (SEK 995) with two people and a van. We carry it out, take it apart if it won't go through the door and drive it to second hand or recycling.",
        },
        {
          question: "Do you collect furniture across all of Gothenburg?",
          answer:
            "Yes, in every district – from Linnéstaden, Majorna and Olskroken to Hisingen, Kortedala, Västra Frölunda and Askim – and in the neighbouring municipalities. Same price everywhere.",
        },
        {
          question: "Can you manage landshövdingehus buildings without a lift?",
          answer:
            "Yes. The timber stairs in Majorna, Kungsladugård and Olskroken are narrow and turn, so we take larger furniture apart and protect steps and walls. No surcharge for floors.",
        },
        {
          question: "Will you take the furniture apart for me?",
          answer:
            "Yes, taking apart is included. We bring tools and dismantle wardrobes, beds, bookcases and desks that need it to get out. If a piece needs special tools, mention it when booking.",
        },
        {
          question: "Can you drive my furniture to second hand in Gothenburg?",
          answer:
            "Yes. Name the recipient in the booking, for example Göteborgs Stadsmission, Myrorna, Erikshjälpen or Emmaus Björkå. It is a good idea to contact the recipient in advance for large pieces.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture removal in Gothenburg?",
          answer:
            "If your furniture is transported to a second-hand shop, charity or another location for reuse, you are eligible for the RUT tax deduction on the labour cost. Standard furniture removal where items go directly to a recycling station does not qualify. The deduction is applied directly on the invoice.",
        },
        {
          question: "Do you collect mattresses?",
          answer:
            "Yes, we take mattresses, toppers and box springs. Used mattresses are rarely accepted by second-hand shops, so they usually go to recycling.",
        },
        {
          question: "Can I leave the furniture in the bulky-waste room instead?",
          answer:
            "That is up to your landlord or association, and many do not allow furniture there. Stairwells and basement corridors must always be kept clear. The easiest option is for us to collect straight from the flat.",
        },
        {
          question: "Do you collect from attic and basement storage?",
          answer:
            "Yes, we collect furniture from attic storage, basements, storage units and garages. Make sure we have a key and tell us where the space is.",
        },
        {
          question: "Do you collect office furniture in Gothenburg?",
          answer:
            "Yes, from home offices and businesses across Gothenburg: desks, office chairs, filing cabinets and shelving. If a whole office needs clearing, we have a separate service for that.",
        },
        {
          question: "How soon can you collect?",
          answer:
            "Often the same day. You see available times directly in the booking and can book up to 30 days ahead, evenings and weekends included.",
        },
        {
          question: "Can you take more than furniture in the same booking?",
          answer:
            "Yes, add boxes, appliances or electronics to the same booking and pick a package by the total amount.",
        },
        {
          question: "Which municipalities in the Gothenburg region do you collect furniture in?",
          answer:
            "We collect furniture across the whole Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No surcharge for neighbouring municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Furniture removal in Gothenburg in 3 steps",
        step2Text:
          "We take apart what needs it, carry the furniture out – even on the timber stairs of the landshövdingehus – and load the van. Intact furniture can go to second hand.",
        ctaHeading: "Book furniture collection in Gothenburg",
        ctaText:
          "Enter the address, pick a package by the number of pieces and choose a time. The price shows instantly, no quote.",
      },
    },
  },
];
